
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Users, User, Loader2 } from "lucide-react";

interface TaskAssignmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tasks: Array<{
    id?: string;
    title: string;
    description?: string;
    priority: string;
    assigned_to?: string;
  }>;
  onAssignmentsChange: (assignments: Record<string, string>) => void;
  onSave: () => void;
  isCreating?: boolean;
}

export const TaskAssignmentModal = ({
  open,
  onOpenChange,
  tasks,
  onAssignmentsChange,
  onSave,
  isCreating = false
}: TaskAssignmentModalProps) => {
  const { session } = useAuth();
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [bulkAssignee, setBulkAssignee] = useState<string>("");

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      if (!session) return [];
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email');
      
      if (error) throw error;
      return data;
    },
    enabled: !!session && open
  });

  const handleAssignment = (taskIndex: number, userId: string) => {
    const newAssignments = {
      ...assignments,
      [taskIndex]: userId === "unassigned" ? "" : userId
    };
    setAssignments(newAssignments);
    onAssignmentsChange(newAssignments);
  };

  const handleBulkAssign = () => {
    if (!bulkAssignee) return;
    
    const newAssignments: Record<string, string> = {};
    tasks.forEach((_, index) => {
      newAssignments[index] = bulkAssignee === "unassigned" ? "" : bulkAssignee;
    });
    setAssignments(newAssignments);
    onAssignmentsChange(newAssignments);
    setBulkAssignee("");
  };

  const handleSave = () => {
    console.log('TaskAssignmentModal - Saving with assignments:', assignments);
    onSave();
  };

  const handleSkip = () => {
    console.log('TaskAssignmentModal - Skipping assignments');
    onSave();
  };

  const getUnassignedCount = () => {
    return tasks.length - Object.keys(assignments).filter(key => assignments[key]).length;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'destructive';
      case 'high':
        return 'destructive';
      case 'medium':
        return 'default';
      case 'low':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Assign Tasks to Team Members
            {isCreating && (
              <div className="flex items-center gap-2 ml-auto">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Creating tasks...</span>
              </div>
            )}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Assign the generated tasks to team members. You can skip this step and assign them manually later.
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Bulk Assignment Section */}
          <div className="border rounded-lg p-4 bg-muted/30">
            <div className="flex items-center gap-2 mb-3">
              <User className="h-4 w-4" />
              <span className="font-medium">Bulk Assignment</span>
              <Badge variant="outline">
                {getUnassignedCount()} unassigned
              </Badge>
            </div>
            <div className="flex gap-2">
              <Select value={bulkAssignee} onValueChange={setBulkAssignee} disabled={isCreating}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select user to assign all tasks" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Leave Unassigned</SelectItem>
                  {users?.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.full_name || user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                onClick={handleBulkAssign} 
                disabled={!bulkAssignee || isCreating}
                variant="outline"
              >
                Assign All
              </Button>
            </div>
          </div>

          {/* Individual Task Assignments */}
          <div className="space-y-3">
            {tasks.map((task, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-2">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium truncate">{task.title}</h4>
                      <Badge variant={getPriorityColor(task.priority)} className="text-xs">
                        {task.priority}
                      </Badge>
                    </div>
                    {task.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {task.description}
                      </p>
                    )}
                  </div>
                  
                  <div className="w-48 flex-shrink-0">
                    <Select
                      value={assignments[index] || "unassigned"}
                      onValueChange={(value) => handleAssignment(index, value)}
                      disabled={isCreating}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {users?.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.full_name || user.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <Button variant="outline" onClick={handleSkip} disabled={isCreating}>
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              "Skip - Assign Later"
            )}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isCreating}>
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Tasks...
                </>
              ) : (
                "Save Tasks with Assignments"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
